import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { 
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  RadioIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Mission, MissionComment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import missionService from '../../services/missionService';

interface CommunicationsTabProps {
  mission: Mission;
  onUpdate: (updates: Partial<Mission>) => void;
  onAddComment: (commentText: string) => void;
  canEdit: boolean;
}

const CommunicationsTab: React.FC<CommunicationsTabProps> = ({ 
  mission, 
  onUpdate,
  onAddComment,
  canEdit 
}) => {
  useAuth();
  useSocket();
  const [newComment, setNewComment] = useState('');
  const [autoScroll] = useState(true);
  const [localValues, setLocalValues] = useState<Partial<Mission>>({});
  const [showAddFrequency, setShowAddFrequency] = useState(false);
  const [newFrequency, setNewFrequency] = useState({ name: '', frequency: '' });
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    if (autoScroll && commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mission.comments, autoScroll]);

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment.trim());
      setNewComment('');
    }
  };

  const formatTimestamp = (timestamp: Date | string) => {
    return format(new Date(timestamp), 'HH:mm:ss');
  };

  const formatDate = (timestamp: Date | string) => {
    return format(new Date(timestamp), 'dd/MM/yyyy');
  };

  const handleFieldChange = (field: keyof Mission, value: any) => {
    setLocalValues({ ...localValues, [field]: value });
  };

  const handleFieldBlur = (field: keyof Mission) => {
    if (localValues[field] !== undefined && localValues[field] !== mission[field]) {
      onUpdate({ [field]: localValues[field] });
      setLocalValues({ ...localValues, [field]: undefined });
    }
  };

  // Group comments by date
  const groupedComments = mission.comments?.reduce((groups, comment) => {
    const date = formatDate(comment.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(comment);
    return groups;
  }, {} as Record<string, MissionComment[]>) || {};

  return (
    <div className="space-y-6">
      {/* Radio Frequencies */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center justify-between">
            <span className="flex items-center">
              <RadioIcon className="h-5 w-5 mr-2" />
              Radio Frequencies
            </span>
            {canEdit && (
              <button
                onClick={() => setShowAddFrequency(true)}
                className="btn btn-primary btn-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Frequency
              </button>
            )}
          </h3>
        </div>
        
        {/* Add Frequency Form */}
        {showAddFrequency && canEdit && (
          <div className="p-4 bg-blue-50 rounded-lg mb-4">
            <h4 className="font-medium text-blue-900 mb-3">Add New Frequency</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="form-label">Frequency Name</label>
                <input
                  type="text"
                  value={newFrequency.name}
                  onChange={(e) => setNewFrequency({ ...newFrequency, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Scene Command"
                />
              </div>
              <div>
                <label className="form-label">Frequency</label>
                <input
                  type="text"
                  value={newFrequency.frequency}
                  onChange={(e) => setNewFrequency({ ...newFrequency, frequency: e.target.value })}
                  className="form-input"
                  placeholder="e.g., 123.450 MHz"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => {
                  setShowAddFrequency(false);
                  setNewFrequency({ name: '', frequency: '' });
                }}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (newFrequency.name && newFrequency.frequency) {
                    try {
                      await missionService.addRadioFrequency(mission.id, {
                        name: newFrequency.name,
                        frequency: newFrequency.frequency,
                        type: 'CUSTOM'
                      });
                      // Refresh the mission to get updated frequencies
                      const updatedMission = await missionService.getMission(mission.id);
                      onUpdate({ radioFrequencies: updatedMission.radioFrequencies });
                      setNewFrequency({ name: '', frequency: '' });
                      setShowAddFrequency(false);
                    } catch (error) {
                      console.error('Failed to add frequency:', error);
                    }
                  }
                }}
                className="btn btn-primary btn-sm"
                disabled={!newFrequency.name || !newFrequency.frequency}
              >
                Add Frequency
              </button>
            </div>
          </div>
        )}
        
        {/* Frequency List */}
        {mission.radioFrequencies && mission.radioFrequencies.length > 0 ? (
          <div className="space-y-4">
            {/* Group by type */}
            {['PRIMARY', 'HOSPITAL', 'CUSTOM'].map(type => {
              const frequencies = mission.radioFrequencies?.filter(f => f.type === type);
              if (!frequencies || frequencies.length === 0) return null;
              
              return (
                <div key={type}>
                  <h4 className="font-medium text-gray-700 mb-2">
                    {type === 'PRIMARY' && 'Primary Frequencies'}
                    {type === 'HOSPITAL' && 'Hospital Frequencies'}
                    {type === 'CUSTOM' && 'Custom Frequencies'}
                  </h4>
                  <div className="space-y-2">
                    {frequencies.map((freq) => (
                      <div key={freq.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <span className="text-sm text-gray-600">{freq.name}:</span>
                          <span className="font-mono font-medium ml-2">{freq.frequency}</span>
                        </div>
                        {canEdit && freq.type === 'CUSTOM' && (
                          <button
                            onClick={async () => {
                              try {
                                await missionService.removeRadioFrequency(mission.id, freq.id);
                                // Update local state
                                const updatedFrequencies = mission.radioFrequencies?.filter(f => f.id !== freq.id);
                                onUpdate({ radioFrequencies: updatedFrequencies });
                              } catch (error) {
                                console.error('Failed to remove frequency:', error);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <RadioIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No frequencies configured</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add radio frequencies for this mission.
            </p>
          </div>
        )}
      </div>

      {/* Comment Log */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
            Mission Communications Log
          </h3>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
          {Object.keys(groupedComments).length > 0 ? (
            Object.entries(groupedComments).map(([date, comments]) => (
              <div key={date} className="mb-4">
                <div className="text-center mb-2">
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                    {date}
                  </span>
                </div>
                
                {comments.map((comment) => (
                  <div key={comment.id} className="mb-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-700">
                            {comment.dispatcherInitials}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700">
                              {comment.dispatcher?.fullName || comment.dispatcherInitials}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatTimestamp(comment.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900">{comment.commentText}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No communications yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start logging mission communications below.
              </p>
            </div>
          )}
          <div ref={commentsEndRef} />
        </div>
        
        {/* Add Comment Form */}
        {canEdit && (
          <form onSubmit={handleSubmitComment} className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="form-input flex-1"
              placeholder="Type a comment..."
              maxLength={500}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!newComment.trim()}
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </form>
        )}
      </div>

      {/* Communications Notes */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Communications Notes
          </h3>
        </div>
        
        <textarea
          value={localValues.communicationsNotes !== undefined ? localValues.communicationsNotes : (mission.communicationsNotes || '')}
          onChange={(e) => handleFieldChange('communicationsNotes', e.target.value)}
          onBlur={() => handleFieldBlur('communicationsNotes')}
          className="form-input"
          rows={4}
          placeholder="Important phone numbers, alternate frequencies, communication issues..."
          disabled={!canEdit}
        />
        
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Communication Protocol</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• All communications logged with timestamp and dispatcher initials</li>
            <li>• Use clear, concise language - avoid codes unless standard</li>
            <li>• Confirm critical information with read-back</li>
            <li>• Document all medical orders and destination changes</li>
            <li>• Note any communication difficulties or equipment issues</li>
          </ul>
        </div>
      </div>

      {canEdit && (
        <div className="text-sm text-gray-500 italic">
          Tip: All fields auto-save when you click away or press Tab
        </div>
      )}
    </div>
  );
};

export default CommunicationsTab;